import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaAt as FaAtRaw,
  FaEllipsisV as FaEllipsisVRaw,
  FaImage as FaImageRaw,
  FaPaperPlane as FaPaperPlaneRaw,
  FaSearch as FaSearchRaw,
  FaSmile as FaSmileRaw,
  FaUserPlus as FaUserPlusRaw,
} from 'react-icons/fa';
import MarketingHeader from '../components/MarketingHeader';
import {
  useCreateMarketingCampaign,
  useMarketingCampaigns,
  useMarketingKeywords,
  useMarketingSubscribers,
  type MarketingSubscriber,
} from '../hooks/useMarketing';
import { supabase } from '../services/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaAt: any = FaAtRaw;
const FaEllipsisV: any = FaEllipsisVRaw;
const FaImage: any = FaImageRaw;
const FaPaperPlane: any = FaPaperPlaneRaw;
const FaSearch: any = FaSearchRaw;
const FaSmile: any = FaSmileRaw;
const FaUserPlus: any = FaUserPlusRaw;

type ConversationTag = 'all' | 'unread' | 'vip';

interface Conversation {
  id: string;
  name: string;
  phone: string;
  isActive: boolean;
  unreadCount: number;
  preview: string;
  timeLabel: string;
  vip: boolean;
  messages: ChatMessage[];
}

interface ChatMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  body: string;
  createdAt: string;
  timestamp: string;
}

interface KeywordEventRow {
  id: string;
  keyword_id: string | null;
  subscriber_id: string | null;
  normalized_phone: string | null;
  created_at: string;
}

const INBOX_BREAKPOINT = '1080px';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 0;
  padding-bottom: 1.5rem;
`;

const Shell = styled.section`
  padding: 0; /* override global section { padding: 90px 0 } */
  display: grid;
  grid-template-columns: 340px 1fr;
  min-height: calc(100vh - 12rem);
  max-height: 720px;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  @media (max-width: ${INBOX_BREAKPOINT}) {
    grid-template-columns: 1fr;
    min-height: 0;
    max-height: none;
  }
`;

const Sidebar = styled.aside`
  border-right: 1px solid var(--outline);
  background: var(--surface);
  min-width: 0;
  display: flex;
  flex-direction: column;
  @media (max-width: ${INBOX_BREAKPOINT}) {
    border-right: none;
    border-bottom: 1px solid var(--outline);
    max-height: 320px;
  }
`;

const SearchWrap = styled.div`
  padding: 1rem 1.25rem 0.75rem;
  flex-shrink: 0;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid var(--outline);
  border-radius: 999px;
  padding: 0.625rem 1rem;
  color: var(--onSurfaceVariant);
  background: var(--surfaceVariant);
  font-size: 0.875rem;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:focus-within {
    border-color: var(--outlineVariant);
    background: var(--elevation2);
  }
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-size: 0.875rem;
  color: var(--onSurface);
  background: transparent;
  &::placeholder {
    color: var(--onSurfaceVariant);
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0 1.25rem 0.75rem;
  flex-shrink: 0;
`;

const FilterPill = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurfaceVariant)')};
  border-radius: 999px;
  padding: 0.375rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) {
    background: ${({ $active }) => ($active ? 'var(--primaryDark)' : 'var(--surfaceVariant)')};
    border-color: var(--outlineVariant);
    color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurface)')};
  }
`;

const List = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  @media (max-width: ${INBOX_BREAKPOINT}) {
    min-height: 180px;
  }
`;

const ConversationItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  border: none;
  border-top: 1px solid var(--outline);
  background: ${({ $active }) => ($active ? 'var(--surfaceVariant)' : 'transparent')};
  text-align: left;
  padding: 1rem 1.25rem;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover {
    background: var(--surfaceVariant);
  }
  &:first-of-type {
    border-top: none;
  }
`;

const RowTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const Name = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--onSurface);
  line-height: 1.3;
`;

const Time = styled.div`
  font-size: 0.75rem;
  color: var(--onSurfaceVariant);
  white-space: nowrap;
  flex-shrink: 0;
`;

const Preview = styled.div`
  font-size: 0.8125rem;
  color: var(--onSurfaceVariant);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
`;

const TagBadge = styled.span<{ $type: 'unread' | 'vip' }>`
  margin-top: 0.5rem;
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  background: ${({ $type }) =>
    $type === 'unread' ? 'var(--primary)' : 'var(--warningContainer)'};
  color: ${({ $type }) =>
    $type === 'unread' ? 'var(--onPrimary)' : 'var(--onWarningContainer)'};
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

const ThreadHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--outline);
  min-height: 72px;
  flex-shrink: 0;
  background: var(--surface);
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  min-width: 0;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 0.75rem;
  color: var(--onPrimary);
  background: var(--primary);
  flex-shrink: 0;
`;

const ContactName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: var(--onSurface);
  line-height: 1.3;
`;

const ContactPhone = styled.div`
  font-size: 0.8125rem;
  color: var(--onSurfaceVariant);
  margin-top: 0.125rem;
`;

const ContactStatus = styled.div`
  font-size: 0.75rem;
  color: var(--success);
  margin-top: 0.125rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--onSurfaceVariant);
  font-size: 1rem;
  flex-shrink: 0;
`;

const ThreadBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1.25rem;
  background: var(--background);
`;

const BubbleRow = styled.div<{ $direction: 'inbound' | 'outbound' }>`
  display: flex;
  justify-content: ${({ $direction }) => ($direction === 'outbound' ? 'flex-end' : 'flex-start')};
  margin-bottom: 1rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const Bubble = styled.div<{ $direction: 'inbound' | 'outbound' }>`
  max-width: min(80%, 420px);
  border-radius: 10px;
  border: 1px solid ${({ $direction }) =>
    $direction === 'outbound' ? 'var(--primaryDark)' : 'var(--outline)'};
  padding: 0.75rem 1rem;
  line-height: 1.5;
  font-size: 0.9375rem;
  color: ${({ $direction }) => ($direction === 'outbound' ? 'var(--onPrimary)' : 'var(--onSurface)')};
  background: ${({ $direction }) =>
    $direction === 'outbound' ? 'var(--primary)' : 'var(--surface)'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
`;

const BubbleMeta = styled.div<{ $direction: 'inbound' | 'outbound' }>`
  margin-top: 0.25rem;
  text-align: ${({ $direction }) => ($direction === 'outbound' ? 'right' : 'left')};
  font-size: 0.6875rem;
  color: var(--onSurfaceVariant);
`;

const Composer = styled.div`
  border-top: 1px solid var(--outline);
  background: var(--surface);
  padding: 1rem 1.25rem 1.25rem;
  flex-shrink: 0;
`;

const ComposerInput = styled.textarea`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 8px;
  min-height: 80px;
  resize: vertical;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  color: var(--onSurface);
  background: var(--surfaceVariant);
  transition: border-color 0.15s ease, background 0.15s ease;
  &::placeholder {
    color: var(--onSurfaceVariant);
  }
  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--surface);
  }
`;

const ComposerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.75rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const QuickActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--onSurfaceVariant);
  font-size: 0.8125rem;
  span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    cursor: default;
  }
`;

const SendGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Counter = styled.div`
  font-size: 0.75rem;
  color: var(--onSurfaceVariant);
`;

const SendButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--onPrimary);
  background: var(--primary);
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover:not(:disabled) {
    background: var(--primaryDark);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const formatRelativeDate = (dateIso: string): string => {
  const date = new Date(dateIso);
  if (Number.isNaN(date.getTime())) return 'Recently';
  const now = Date.now();
  const diffMs = now - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatMessageTime = (dateIso: string): string => {
  const date = new Date(dateIso);
  if (Number.isNaN(date.getTime())) return 'Recently';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

export default function MarketingInboxPage() {
  const subscribersQuery = useMarketingSubscribers();
  const campaignsQuery = useMarketingCampaigns();
  const keywordsQuery = useMarketingKeywords();
  const createCampaign = useCreateMarketingCampaign();

  const keywordEventsQuery = useQuery<KeywordEventRow[]>({
    queryKey: ['marketing', 'keyword-events'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('marketing_keyword_events')
        .select('id, keyword_id, subscriber_id, normalized_phone, created_at')
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as KeywordEventRow[];
    },
    staleTime: 30_000,
  });

  const [activeConversationId, setActiveConversationId] = useState('');
  const [filter, setFilter] = useState<ConversationTag>('all');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [localMessagesByPhone, setLocalMessagesByPhone] = useState<Record<string, ChatMessage[]>>({});
  const [isSending, setIsSending] = useState(false);

  const keywordMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of keywordsQuery.data ?? []) {
      map.set(item.id, item.keyword);
    }
    return map;
  }, [keywordsQuery.data]);

  const subscriberById = useMemo(() => {
    const map = new Map<string, MarketingSubscriber>();
    for (const item of subscribersQuery.data ?? []) {
      map.set(item.id, item);
    }
    return map;
  }, [subscribersQuery.data]);

  const subscriberByPhone = useMemo(() => {
    const map = new Map<string, MarketingSubscriber>();
    for (const item of subscribersQuery.data ?? []) {
      map.set(item.phone_e164, item);
    }
    return map;
  }, [subscribersQuery.data]);

  const conversations = useMemo(() => {
    const byPhone = new Map<string, Conversation>();

    const getDisplayName = (phone: string, subscriber?: MarketingSubscriber) => {
      if (subscriber) {
        const fullName = `${subscriber.first_name ?? ''} ${subscriber.last_name ?? ''}`.trim();
        if (fullName) return fullName;
      }
      return phone;
    };

    const upsertConversation = (phone: string, message: ChatMessage) => {
      const subscriber = subscriberByPhone.get(phone);
      const existing = byPhone.get(phone);
      const isVip = Boolean(
        subscriber?.channel_memberships?.some((membership) =>
          (membership.channel?.name ?? '').toLowerCase().includes('vip')
        )
      );

      if (existing) {
        existing.messages.push(message);
        return;
      }

      byPhone.set(phone, {
        id: phone,
        name: getDisplayName(phone, subscriber),
        phone,
        isActive: subscriber?.is_active ?? true,
        unreadCount: 0,
        preview: message.body,
        timeLabel: message.timestamp,
        vip: isVip,
        messages: [message],
      });
    };

    for (const event of keywordEventsQuery.data ?? []) {
      const subscriber = event.subscriber_id ? subscriberById.get(event.subscriber_id) : undefined;
      const phone = event.normalized_phone ?? subscriber?.phone_e164 ?? '';
      if (!phone) continue;
      const keyword = event.keyword_id ? keywordMap.get(event.keyword_id) : null;
      const inboundBody = keyword ? keyword.toUpperCase() : 'Inbound keyword';
      upsertConversation(phone, {
        id: `inbound-${event.id}`,
        direction: 'inbound',
        body: inboundBody,
        createdAt: event.created_at,
        timestamp: formatMessageTime(event.created_at),
      });
    }

    for (const campaign of campaignsQuery.data ?? []) {
      const metadata = (campaign.metadata ?? {}) as Record<string, unknown>;
      const body = typeof metadata.body === 'string' ? metadata.body.trim() : '';
      if (!body) continue;
      const recipientPhones = Array.isArray(metadata.recipientPhones)
        ? metadata.recipientPhones.filter((value): value is string => typeof value === 'string')
        : [];
      const launchMeta =
        typeof metadata.twilio_last_launch === 'object' && metadata.twilio_last_launch != null
          ? (metadata.twilio_last_launch as Record<string, unknown>)
          : {};
      const createdAt =
        typeof launchMeta.launched_at === 'string' ? launchMeta.launched_at : campaign.created_at;
      for (const phone of recipientPhones) {
        upsertConversation(phone, {
          id: `outbound-${campaign.id}-${phone}`,
          direction: 'outbound',
          body,
          createdAt,
          timestamp: formatMessageTime(createdAt),
        });
      }
    }

    for (const subscriber of subscribersQuery.data ?? []) {
      if (byPhone.has(subscriber.phone_e164)) continue;
      byPhone.set(subscriber.phone_e164, {
        id: subscriber.phone_e164,
        name: `${subscriber.first_name ?? ''} ${subscriber.last_name ?? ''}`.trim() || subscriber.phone_e164,
        phone: subscriber.phone_e164,
        isActive: subscriber.is_active,
        unreadCount: 0,
        preview: 'No messages yet.',
        timeLabel: 'Recently',
        vip: Boolean(
          subscriber.channel_memberships?.some((membership) =>
            (membership.channel?.name ?? '').toLowerCase().includes('vip')
          )
        ),
        messages: [],
      });
    }

    const nowMs = Date.now();
    const twentyFourHoursMs = 24 * 60 * 60 * 1000;

    const normalized = Array.from(byPhone.values()).map((conversation) => {
      const local = localMessagesByPhone[conversation.phone] ?? [];
      const merged = [...conversation.messages, ...local].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      const latest = merged[merged.length - 1];
      const unreadCount = merged.filter((message) => {
        if (message.direction !== 'inbound') return false;
        const ts = new Date(message.createdAt).getTime();
        return Number.isFinite(ts) && nowMs - ts <= twentyFourHoursMs;
      }).length;
      return {
        ...conversation,
        messages: merged,
        unreadCount,
        preview: latest?.body ?? conversation.preview,
        timeLabel: latest ? formatRelativeDate(latest.createdAt) : conversation.timeLabel,
      };
    });

    normalized.sort((a, b) => {
      const aTime = new Date(a.messages[a.messages.length - 1]?.createdAt ?? 0).getTime();
      const bTime = new Date(b.messages[b.messages.length - 1]?.createdAt ?? 0).getTime();
      return bTime - aTime;
    });

    return normalized;
  }, [
    campaignsQuery.data,
    keywordEventsQuery.data,
    keywordMap,
    localMessagesByPhone,
    subscriberById,
    subscriberByPhone,
    subscribersQuery.data,
  ]);

  useEffect(() => {
    if (!conversations.length) {
      setActiveConversationId('');
      return;
    }
    const exists = conversations.some((conversation) => conversation.id === activeConversationId);
    if (!exists) {
      setActiveConversationId(conversations[0].id);
    }
  }, [activeConversationId, conversations]);

  const visibleConversations = useMemo(() => {
    return conversations.filter((item) => {
      if (filter === 'unread' && item.unreadCount <= 0) return false;
      if (filter === 'vip' && !item.vip) return false;
      if (!search.trim()) return true;
      const term = search.trim().toLowerCase();
      return item.name.toLowerCase().includes(term) || item.preview.toLowerCase().includes(term);
    });
  }, [conversations, filter, search]);

  const selectedConversation =
    visibleConversations.find((item) => item.id === activeConversationId) ?? visibleConversations[0] ?? null;
  const selectedMessages = selectedConversation?.messages ?? [];
  const isLoading =
    subscribersQuery.isLoading || campaignsQuery.isLoading || keywordsQuery.isLoading || keywordEventsQuery.isLoading;
  const hasQueryError =
    subscribersQuery.isError || campaignsQuery.isError || keywordsQuery.isError || keywordEventsQuery.isError;

  const handleSend = async () => {
    if (!selectedConversation || !draft.trim() || isSending) return;
    setIsSending(true);
    const body = draft.trim();
    try {
      const campaign = await createCampaign.mutateAsync({
        name: `Inbox reply to ${selectedConversation.phone}`,
        channel: 'sms',
        status: 'scheduled',
        metadata: {
          body,
          recipientPhones: [selectedConversation.phone],
          source: 'marketing_inbox_reply',
        },
      });
      const { error, data } = await supabase.functions.invoke('launch-marketing-campaign', {
        body: { campaign_id: campaign.id },
      });
      if (error) {
        throw new Error(error.message || 'Failed to send message.');
      }
      if (data && typeof data === 'object' && 'error' in data && (data as { error?: string }).error) {
        throw new Error((data as { error: string }).error);
      }

      const createdAt = new Date().toISOString();
      const localMessage: ChatMessage = {
        id: `local-${campaign.id}`,
        direction: 'outbound',
        body,
        createdAt,
        timestamp: formatMessageTime(createdAt),
      };
      setLocalMessagesByPhone((prev) => ({
        ...prev,
        [selectedConversation.phone]: [...(prev[selectedConversation.phone] ?? []), localMessage],
      }));
      setDraft('');
      campaignsQuery.refetch();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Failed to send message.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Page>
      <MarketingHeader activeTab="inbox" />
      <Shell>
        <Sidebar>
          <SearchWrap>
            <SearchBar>
              <FaSearch />
              <SearchInput
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search conversations..."
              />
            </SearchBar>
          </SearchWrap>
          <FilterRow>
            <FilterPill $active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterPill>
            <FilterPill $active={filter === 'unread'} onClick={() => setFilter('unread')}>Unread</FilterPill>
            <FilterPill $active={filter === 'vip'} onClick={() => setFilter('vip')}>VIP</FilterPill>
          </FilterRow>
          <List>
            {isLoading && (
              <div style={{ padding: '1.25rem', color: 'var(--onSurfaceVariant)', fontSize: '0.875rem' }}>
                Loading conversations...
              </div>
            )}
            {hasQueryError && !isLoading && (
              <div style={{ padding: '1.25rem', color: 'var(--error)', fontSize: '0.875rem' }}>
                Unable to load inbox data.
              </div>
            )}
            {!isLoading && !hasQueryError && visibleConversations.length === 0 && (
              <div style={{ padding: '1.25rem', color: 'var(--onSurfaceVariant)', fontSize: '0.875rem' }}>
                No matching conversations.
              </div>
            )}
            {visibleConversations.map((item) => (
              <ConversationItem
                key={item.id}
                $active={selectedConversation?.id === item.id}
                onClick={() => setActiveConversationId(item.id)}
              >
                <RowTop>
                  <Name>{item.name}</Name>
                  <Time>{item.timeLabel}</Time>
                </RowTop>
                <Preview>{item.preview}</Preview>
                {item.unreadCount > 0 ? <TagBadge $type="unread">UNREAD</TagBadge> : null}
                {item.unreadCount === 0 && item.vip ? <TagBadge $type="vip">VIP</TagBadge> : null}
              </ConversationItem>
            ))}
          </List>
        </Sidebar>

        <Thread>
          <ThreadHeader>
            <ContactInfo>
              <Avatar>+1</Avatar>
              <div>
                <ContactName>{selectedConversation?.name ?? 'No conversation selected'}</ContactName>
                <ContactPhone>{selectedConversation?.phone ?? ''}</ContactPhone>
                <ContactStatus>
                  {selectedConversation
                    ? selectedConversation.isActive
                      ? 'Active Subscriber'
                      : 'Inactive Subscriber'
                    : ''}
                </ContactStatus>
              </div>
            </ContactInfo>
            <HeaderActions>
              <FaUserPlus />
              <FaEllipsisV />
            </HeaderActions>
          </ThreadHeader>

          <ThreadBody>
            {selectedMessages.map((message) => (
              <BubbleRow key={message.id} $direction={message.direction}>
                <div>
                  <Bubble $direction={message.direction}>{message.body}</Bubble>
                  <BubbleMeta $direction={message.direction}>
                    {message.timestamp}
                    {message.direction === 'outbound' ? '  · Sent' : ''}
                  </BubbleMeta>
                </div>
              </BubbleRow>
            ))}
          </ThreadBody>

          <Composer>
            <ComposerInput
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Type your message..."
            />
            <ComposerFooter>
              <QuickActions>
                <span><FaAt /> Tags</span>
                <span><FaSmile /> Emoji</span>
                <span><FaImage /> Media</span>
              </QuickActions>
              <SendGroup>
                <Counter>{draft.length} / 160 (1 segment)</Counter>
                <SendButton type="button" onClick={handleSend} disabled={!selectedConversation || !draft.trim() || isSending}>
                  Send <FaPaperPlane size={12} />
                </SendButton>
              </SendGroup>
            </ComposerFooter>
          </Composer>
        </Thread>
      </Shell>
    </Page>
  );
}
